import { z } from 'zod';
import { ANALYST_LEVELS, analystLevelSchema } from './analyst.js';
import {
  NOYAU_CODES,
  type NoyauCode,
  type NoyauScore,
  isNoyauCode,
  noyauCodeSchema,
  noyauDeterminationStateSchema,
  oppositeNoyau,
} from './noyau.js';

/**
 * The EXPECTED noyau against the FOUND one (npc.md §26, issue #216, lot H): the
 * pure rules that compare them and the response shapes that carry the result.
 *
 * ## Why this file imports nothing but Zod and the two leaves
 *
 * `npc.ts` and `replay-mark.ts` carry the shapes below, and `replay-mark.ts`
 * stands on nothing but Zod so that the list row and the detail of a session can
 * cite it. This file cites `analyst.ts` and `noyau.ts`, which are themselves
 * leaves, and nothing of theirs cites it back: the dependency runs one way.
 *
 * ## Nothing here is stored (§26.9, H32)
 *
 * Every shape is a RESPONSE: the server computes the verdict at the reading, off
 * the version of the NPC the replay played, its determination and the row of its
 * first choice. No stored schema changes, so no row in the base is invalidated;
 * the fields these shapes add to the responses are optional and nullable, and a
 * caller written before them keeps parsing.
 *
 * ## One function says the verdict, the screens and the assistant read it
 *
 * `compareNoyaux` and `placeExpectedNoyau` are the only places the verdict, the
 * rank and the gap are computed (A3, A8): the parcours, the tab of the NPC, the
 * list, the exports and `get_session` all serve what they return.
 */

/* ----------------------------------------------------------- the expected */

/**
 * The expected noyau of an NPC (H6): one of the six triplets, or « hors base »,
 * or nothing at all (the field is `null`). Moved here from `npc.ts` by #216, the
 * response shapes below needing it and `npc.ts` importing them.
 *
 * Since #216 it is compared, on a replay, to the noyau the session found - and
 * only there: an expected noyau is the one of the manual scenario, and nothing
 * recalibrates it on what the machine returns (§26.1).
 */
export const OUT_OF_BASE = 'out-of-base' as const;
export const npcExpectedNoyauSchema = z.union([noyauCodeSchema, z.literal(OUT_OF_BASE)]);
export type NpcExpectedNoyau = z.infer<typeof npcExpectedNoyauSchema>;

/* ------------------------------------------------------------ the verdict */

/**
 * The four verdicts (§26.2, A3). Two distinct noyaux share at most one level
 * (the triplets are permutations of S, G, P), so a comparison is read on the
 * number of levels that hold:
 *
 * - `same` - the three levels hold;
 * - `near` - one level holds, and it is not the opposite;
 * - `mirror` - the found noyau is the OPPOSITE of the expected one
 *   (`oppositeNoyau`, noyau.md §2.2): only the Comment holds;
 * - `none` - no letter is at its place.
 */
export const NOYAU_VERDICTS = ['same', 'near', 'mirror', 'none'] as const;
export const noyauVerdictSchema = z.enum(NOYAU_VERDICTS);
export type NoyauVerdict = z.infer<typeof noyauVerdictSchema>;

/** Level by level: whether the expected and the found noyau carry the same letter there. */
export const noyauLevelHoldsSchema = z
  .object({ why: z.boolean(), how: z.boolean(), what: z.boolean() })
  .strict();
export type NoyauLevelHolds = z.infer<typeof noyauLevelHoldsSchema>;

/**
 * What comparing two noyaux says: the verdict, the three levels, and for a
 * `near` the one level that holds (`why` or `what`: a `near` that held the
 * Comment would be the mirror). `heldLevel` is null for the three other verdicts.
 */
export const noyauComparisonSchema = z
  .object({
    verdict: noyauVerdictSchema,
    levels: noyauLevelHoldsSchema,
    heldLevel: analystLevelSchema.nullable(),
  })
  .strict();
export type NoyauComparison = z.infer<typeof noyauComparisonSchema>;

/**
 * THE verdict of the expected noyau against the found one (§26.2, A3) - the one
 * function that says it. Pure.
 *
 * It answers null when there is nothing to compare: an expected noyau that is
 * absent or « hors base », or no found noyau (a determination not rendered, no
 * first choice taken yet is the caller's business: it hands in the retained one).
 */
export function compareNoyaux(
  expected: NpcExpectedNoyau | null | undefined,
  found: NoyauCode | null | undefined,
): NoyauComparison | null {
  if (expected == null || found == null) return null;
  if (!isNoyauCode(expected) || !isNoyauCode(found)) return null;

  const holds = ANALYST_LEVELS.map((_, index) => expected[index] === found[index]);
  const levels: NoyauLevelHolds = {
    why: holds[0] === true,
    how: holds[1] === true,
    what: holds[2] === true,
  };
  const heldCount = holds.filter(Boolean).length;

  if (heldCount === ANALYST_LEVELS.length) return { verdict: 'same', levels, heldLevel: null };
  if (found === oppositeNoyau(expected)) return { verdict: 'mirror', levels, heldLevel: null };
  if (heldCount === 0) return { verdict: 'none', levels, heldLevel: null };
  const held = ANALYST_LEVELS.find((level) => levels[level]) ?? null;
  return { verdict: 'near', levels, heldLevel: held };
}

/* ----------------------------------------------------------- the placement */

/**
 * What a gap is measured to: the `retained` noyau (the expected one is not it),
 * or the `second` - the best of the OTHER notes - when the expected noyau IS the
 * retained one (H33).
 */
export const noyauGapTargetSchema = z.enum(['retained', 'second']);
export type NoyauGapTarget = z.infer<typeof noyauGapTargetSchema>;

/**
 * Where the expected noyau falls (§26.2, A4, H33, H34): its rank among the six
 * notes, the gap in points, and whether it was one of the two cards of the first
 * choice.
 *
 * - `rank` - 1 to 6, the best note first: one more than the number of notes
 *   strictly above it, so two noyaux at the same note share a rank;
 * - `tied` - another noyau has the very same note (« 2e ex aequo »);
 * - `gap` - the note of the retained noyau minus the note of the expected one,
 *   zero included; when the expected noyau IS the retained one, its note minus
 *   the best of the other five (`gapTo: 'second'`), negative only when the
 *   retained noyau was not the best-noted one (a card taken within the threshold);
 * - `onCards` - whether the expected noyau was one of the two cards, null when
 *   the pair is not known (an experience without a first choice, a replay that
 *   stopped before it: the fact is omitted, H34).
 */
export const noyauPlacementSchema = z
  .object({
    rank: z.number().int().min(1).max(NOYAU_CODES.length),
    tied: z.boolean(),
    gap: z.number().int().min(-100).max(100),
    gapTo: noyauGapTargetSchema,
    onCards: z.boolean().nullable(),
  })
  .strict();
export type NoyauPlacement = z.infer<typeof noyauPlacementSchema>;

/** What `placeExpectedNoyau` reads. */
export type NoyauPlacementInput = {
  /** The six notes of the determination (only the noyau and its note are read). */
  readonly scores: readonly Pick<NoyauScore, 'noyau' | 'score'>[];
  readonly expected: NoyauCode;
  /** The noyau the session found: its retained one, or its principal without a first choice (H34). */
  readonly retained: NoyauCode;
  /** The two noyaux of the cards of the first choice, null or absent when the pair is unknown. */
  readonly cards?: readonly NoyauCode[] | null | undefined;
};

/**
 * Where the expected noyau falls among the six notes (§26.2, A4) - the one
 * function that says it. Pure.
 *
 * It answers null when the notes cannot say: the expected or the retained noyau
 * has no note, or there is no other note to measure a retained expected noyau to.
 */
export function placeExpectedNoyau(input: NoyauPlacementInput): NoyauPlacement | null {
  const { scores, expected, retained, cards } = input;
  const noteOf = (code: NoyauCode): number | undefined =>
    scores.find((entry) => entry.noyau === code)?.score;
  const expectedNote = noteOf(expected);
  const retainedNote = noteOf(retained);
  if (expectedNote === undefined || retainedNote === undefined) return null;

  const others = scores.filter((entry) => entry.noyau !== expected);
  const rank = 1 + others.filter((entry) => entry.score > expectedNote).length;
  const tied = others.some((entry) => entry.score === expectedNote);
  const onCards = cards == null || cards.length === 0 ? null : cards.includes(expected);

  if (expected !== retained) {
    return { rank, tied, gap: retainedNote - expectedNote, gapTo: 'retained', onCards };
  }
  if (others.length === 0) return null;
  const bestOther = Math.max(...others.map((entry) => entry.score));
  return { rank, tied, gap: expectedNote - bestOther, gapTo: 'second', onCards };
}

/* ------------------------------------------------------------ the response */

/**
 * Which version of the NPC the expected noyau was read on (§26.2, A7): the one
 * the replay PLAYED, or, on a replay with no played version (attached after the
 * fact, « joué avant les NPC »), the current development version - which every
 * screen then marks « attendu actuel ».
 */
export const noyauExpectedProvenanceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('played'), versionNumber: z.number().int().positive() }).strict(),
  z.object({ kind: z.literal('current'), versionNumber: z.number().int().positive() }).strict(),
]);
export type NoyauExpectedProvenance = z.infer<typeof noyauExpectedProvenanceSchema>;

/**
 * The expected noyau on a LINE of the list of sessions, in the mark of a replay
 * (§26.6, H37): the expected noyau (a triplet or « hors base »), its provenance,
 * the noyau found, and the verdict.
 *
 * - `found` - the retained noyau of the session, null when there is none;
 * - `determinationState` - how the determination stands, null without one: it
 *   words the absence of a found noyau (« sans extrait », « en attente »,
 *   « en échec », §26.3);
 * - `comparison` - null when there is nothing to compare (`compareNoyaux`).
 */
export const expectedNoyauMarkSchema = z
  .object({
    expected: npcExpectedNoyauSchema,
    provenance: noyauExpectedProvenanceSchema,
    found: noyauCodeSchema.nullable(),
    determinationState: noyauDeterminationStateSchema.nullable(),
    comparison: noyauComparisonSchema.nullable(),
  })
  .strict();
export type ExpectedNoyauMark = z.infer<typeof expectedNoyauMarkSchema>;

/**
 * The expected noyau READ in full (§26.8, §26.9): the mark, and where the
 * expected noyau falls.
 *
 * - `principal` - the principal of the analyst, only when it differs from the
 *   found noyau (a card taken within the threshold made the second retained,
 *   §26.2), null otherwise;
 * - `placement` - null when the notes cannot say it (`placeExpectedNoyau`) or the
 *   expected noyau is « hors base ».
 *
 * Carried by the block `replay` of a session's detail and by the rows of the tab
 * « Rejeux » and of a series.
 */
export const expectedNoyauReadingSchema = expectedNoyauMarkSchema
  .extend({
    principal: noyauCodeSchema.nullable(),
    placement: noyauPlacementSchema.nullable(),
  })
  .strict();
export type ExpectedNoyauReading = z.infer<typeof expectedNoyauReadingSchema>;

/**
 * One note of the six as a row of the tab « Rejeux » carries it, for the grid of
 * §26.4 (a column per noyau): the noyau and its note, nothing of the analyst's
 * sentences.
 */
export const noyauGridScoreSchema = z
  .object({ noyau: noyauCodeSchema, score: z.number().int().min(0).max(100) })
  .strict();
export type NoyauGridScore = z.infer<typeof noyauGridScoreSchema>;
